import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { jsPDF } from 'jspdf';
import { 
    useMediaQuery, 
    useTheme, 
    Box, 
    Typography, 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,     
} from '@mui/material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { setTimeMode } from "state";
import { AIRLINE_COUNTRY_CODE } from 'state/constants';
import api from "state/axios";
import { getFlagUrl, formatTZDate } from 'state/helpers';
import FlexBetween from 'components/FlexBetween'; 
import CrewListDialog from 'components/CrewListDialog';
import VietnamFlag from "img/VietnamFlag.png";
import UnitedNationsFlag from "img/UnitedNationsFlag.png";


const RosterDetailsDialog = ({ 
    open, 
    onClose, 
    crewCode, 
    crewName, 
    crewRosters,
}) => {
    const dispatch = useDispatch();
    const timeMode = useSelector((state) => state.timemode);
    const params = useSelector((state) => state.params);
    const theme = useTheme();
    const neutralDark = theme.palette.neutral.dark;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const { startDate, endDate } = useDateRangeContext();   

    const [openCrewListDialog, setOpenCrewListDialog] = useState(false);
    const [flight, setFlight] = useState([]);
    const [crewList, setCrewList] = useState([]);
    const [jumpList, setJumpList] = useState([]);

    const paramsMap = useMemo(
        () => new Map(params?.map(p => [p.param, p.value]) ?? []),
        [params]
    );
    const INSTRUCTOR_SIMULATOR_CODES = paramsMap.get("INSTRUCTOR_SIMULATOR_CODES")?.split(',').map(item => item.trim()) || [];
    const AIRLINE_NATIONAL_CODE = paramsMap.get("AIRLINE_COUNTRY_CODE") || "VN";   
    const CARRIER_NATIONAL_CODE = AIRLINE_NATIONAL_CODE || AIRLINE_COUNTRY_CODE;
    const flagUrl = timeMode === "UTC" 
        ? (getFlagUrl("un") || UnitedNationsFlag) 
        : (getFlagUrl(CARRIER_NATIONAL_CODE) || VietnamFlag);
      

    const getDay = (flight, timeMode, field) => 
        timeMode === 'UTC' ? flight[`${field}Day`] : flight[`${field}BaseDay`];

    const getTime = (flight, timeMode, field) => 
        timeMode === 'UTC' ? flight[field] : flight[`${field}Base`];

    const calcFigures = (obj) => {
        let totalBlockMinutes = 0;
        let totalSectors = 0;
        let totalSimulatorMinutes = 0;

        Object.values(obj).forEach(rosters => {
            rosters.forEach(roster => {
                if (roster.blockTime !== null && !roster.pax && roster.routeNo) {
                    totalBlockMinutes += roster.blockMinutes;
                    totalSectors += 1; 
                }
                if (roster.blockMinutes && INSTRUCTOR_SIMULATOR_CODES?.includes(roster.flightNo)) {
                    totalSimulatorMinutes += roster.blockMinutes;
                }
            });
        });

        return {
            totalBlockHours: (totalBlockMinutes / 60).toFixed(2),
            totalSectors,
            totalSimulatorHours: (totalSimulatorMinutes / 60).toFixed(2),
        };
    };

    const { totalBlockHours, totalSimulatorHours } = calcFigures(crewRosters);


    const exportToPDF = () => {
        const doc = new jsPDF();
        let yOffset = 20;
        let xOffset = 20;
        let pageCount = 0;

        const addPageNumber = (pageNum) => {
            const pageText = `Page ${pageNum}`;
            const textWidth = doc.getTextWidth(pageText);
            const xCenter = (doc.internal.pageSize.width - textWidth) / 2;
            const yBottom = doc.internal.pageSize.height - 10; // 10 units from bottom
            doc.text(pageText, xCenter, yBottom);
        };

        const drawLine = (yPosition) => {
            doc.line(xOffset, yPosition, doc.internal.pageSize.width - xOffset, yPosition); // Draw line
        };

        const startDay = formatTZDate(startDate, 'UTC', 'ddMMMyy');
        const endDay = formatTZDate(endDate, 'UTC', 'ddMMMyy');

        // Add title
        const title = `CREW ROSTER`;
        const titleWidth = doc.getTextWidth(title);
        const xCenter = (doc.internal.pageSize.width - titleWidth) / 2;

        doc.setFontSize(20);
        doc.setFont("helvetica", "bold");
        doc.text(title, xCenter, yOffset); // Centered text
        yOffset += 15;

        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text(`${crewCode} - ${crewName}`, xOffset, yOffset);
        yOffset += 10;

        doc.setFont("helvetica", "normal");
        doc.text(`Period: ${startDay} - ${endDay} (Time Mode: ${timeMode})`, xOffset, yOffset);
        yOffset += 10;

        // Draw a line 
        drawLine(yOffset);
        yOffset += 10;

        // Iterate through grouped rosters
        doc.setFontSize(16);
        Object.entries(crewRosters).forEach(([key, flights]) => {                        
            flights.forEach((flight) => {        
                const {
                    routeNo, 
                    gdBegDay,
                    gdEndDay,
                    flightNo,
                    dep,
                    arr,
                    pax,
                    std,
                    sta,
                    stdDay,
                    stdBase,
                    staBase,
                    atd,
                    ata,
                    atdDay,
                    atdBase,
                    ataBase,
                } = flight


                const safeFlightNo = flightNo.padEnd(6, " ");
                const safeDep = dep ?? '';
                const safeArr = arr ?? '';
                const safePax = pax ?? '';
                        
                if (routeNo) {
                    if (gdBegDay) {
                        doc.text(`${getDay(flight, timeMode, 'gdBeg')}`, xOffset, yOffset);
                        yOffset += 10;
                    }
                } else if (atdDay) {
                    doc.text(`${getDay(flight, timeMode, 'atd')}`, xOffset, yOffset);                    
                    yOffset += 10;
                } else {
                    doc.text(`${stdDay}`, xOffset, yOffset);                    
                    yOffset += 10;
                }
              
                if (routeNo && gdBegDay) {
                    doc.text(`Reporting time: ${getTime(flight, timeMode, 'gdBeg')}`, xOffset + 10, yOffset);
                    yOffset += 10;
                } 
                
                const atdOrStd = timeMode === 'UTC' 
                    ? (atd || std || '') 
                    : (atdBase || stdBase || '');
               
                const ataOrSta = timeMode === 'UTC' 
                    ? (ata || sta || '') 
                    : (ataBase || staBase || '');    
                        
                    
                const flightText = `${safeFlightNo}\t${atdOrStd}\t${safeDep}\t${safeArr}\t${ataOrSta}\t${safePax}`;
                doc.text(flightText, xOffset + 10, yOffset);
                yOffset += 10;
                               
                
                if (routeNo && gdEndDay) {
                    doc.text(`Debriefing time: ${getTime(flight, timeMode, 'gdEnd')}`, xOffset + 10, yOffset);
                    yOffset += 10;
                } 

                if (yOffset > doc.internal.pageSize.height - 40) {
                    addPageNumber(++pageCount);
                    doc.addPage();
                    yOffset = 20; // Reset yOffset for new page
                }
            });
           
            // Draw a line 
            drawLine(yOffset);
            yOffset += 10;

            // Add a page break if necessary
            if (yOffset > doc.internal.pageSize.height - 20) {
                addPageNumber(++pageCount);
                doc.addPage();
                yOffset = 20; // Reset yOffset for new page
            }
        });

        // Add the last page number
        addPageNumber(++pageCount);

        // Format and save the filename    
        const filename = `CrewRoster_${crewCode}_${startDay}_${endDay}.pdf`;
        doc.save(filename);
    };
    
    const handleViewClick = useCallback(async (flightDate, flightNo, dep) => {
        try {
            const url = '/legmembers/legmembersbyflightno';
            const payload = { flightDate, flightNo, dep };
            
            const response = await api.post(url, payload);            
            const { flightDetails, legMembers, jumpSeaters } = response.data;
            
            setFlight(flightDetails);
            setCrewList(legMembers);
            setJumpList(jumpSeaters);
            setOpenCrewListDialog(true);
        } catch (error) {
            setOpenCrewListDialog(false);
        } 
    }, []);
    
    const handleCloseCrewListDialog = () => {
        setFlight([]);
        setCrewList([]);
        setJumpList([]);
        setOpenCrewListDialog(false);
    }; 
    
    
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '96%' : '55%' } }}
        >
            <DialogTitle borderBottom={`1px solid ${neutralDark}`}>
                <FlexBetween>
                    <Typography fontWeight="bold" color="primary">
                        {crewCode} - {crewName} 
                    </Typography>
                    <Box onClick={() => dispatch(setTimeMode())}>
                        <img
                            alt="Flag"
                            width={25}
                            height={25}
                            src={flagUrl}
                            style={{ 
                                cursor: "pointer", 
                                objectFit: "cover", 
                                borderRadius: "50%",
                                transition: "opacity 0.3s",
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        />
                    </Box>
                </FlexBetween>
            </DialogTitle>

            <DialogContent>
                {!crewRosters && (
                    <Typography>No rosters available</Typography>
                )} 
                {Object.entries(crewRosters)?.map(([key, flights]) => (
                    <Box 
                        key={key} 
                        p={0.5} 
                        borderBottom={`1px solid ${theme.palette.neutral.dark}`}
                    >
                        {flights?.map((flight, idx) => {
                            const {
                                routeNo, 
                                gdBegDay,
                                gdEndDay,
                                flightDate,
                                flightNo,
                                stdDay,
                                atdDay,
                                dep,
                                arr,
                                pax,
                                trnDuty,
                            } = flight

                            const day = routeNo
                                ? (gdBegDay && getDay(flight, timeMode, "gdBeg"))
                                : (atdDay
                                    ? getDay(flight, timeMode, "atd")
                                    : stdDay);

                            const reportingTime = routeNo && gdBegDay
                                ? getTime(flight, timeMode, "gdBeg")
                                : null;

                            const debriefingTime = routeNo && gdEndDay
                                ? getTime(flight, timeMode, "gdEnd")
                                : null;

                            const atd = getTime(flight, timeMode, "atd");
                            const ata = getTime(flight, timeMode, "ata");
                           
                            return (
                                <Box key={idx}>
                                    <Typography variant="h6" color="primary">
                                        {routeNo && day ? `${day} | Route: ${routeNo}` : day}
                                    </Typography>

                                    {reportingTime && (
                                        <Typography variant="h6" p="0 8px">
                                            Reporting time: {reportingTime}
                                        </Typography>
                                    )}

                                    <FlexBetween gap={1} px={1}>
                                        <Typography
                                            width="30px"
                                            textAlign="left"
                                            color="primary"
                                            sx={{ "&:hover": { cursor: "pointer" } }}
                                            onClick={() =>
                                                handleViewClick(flightDate, Number(flightNo), dep)
                                            }
                                        >
                                            {flightNo}
                                        </Typography>
                                        <Typography width="30px" textAlign="center">{atd}</Typography>
                                        <Typography width="30px" textAlign="center">{dep}</Typography>
                                        <Typography width="30px" textAlign="center">{arr}</Typography>
                                        <Typography width="30px" textAlign="center">{ata}</Typography>
                                        <Typography width="10px" textAlign="center">
                                            {pax && "*"}
                                        </Typography>
                                        <Typography width="10px" textAlign="center">
                                            {trnDuty}
                                        </Typography>
                                    </FlexBetween>

                                    {debriefingTime && (
                                        <Typography variant="h6" p="0 8px">
                                            Debriefing time: {debriefingTime}
                                        </Typography>
                                    )}
                                </Box>
                            );
                            })}
                    </Box>
                    ))
                }
            </DialogContent>

            <DialogActions sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                {crewRosters && 
                    <Box 
                        sx={{
                            display: 'inline-block', 
                            p: 0.5, 
                            borderBottom: '1px solid', 
                            borderColor: 'primary.main',
                        }}
                    >
                        <Typography>
                            BH: {totalBlockHours} 
                            {totalSimulatorHours > 0 && 
                                `, SH: ${totalSimulatorHours}`
                            }
                        </Typography>
                    </Box>
                }
                <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Button variant="outlined" onClick={exportToPDF}>
                        PDF
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={onClose}>
                        Close
                    </Button>
                </Box>
            </DialogActions>

            {openCrewListDialog &&
                <CrewListDialog 
                    open={openCrewListDialog}
                    onClose={handleCloseCrewListDialog}
                    flight={flight}
                    crewList={crewList}
                    jumpList={jumpList}
                />
            }
        </Dialog>
    );
};

export default RosterDetailsDialog;
